import { z } from "zod/v4";
import {
	type AddEventInvitees,
	eventInviteeSchema,
	eventMessageReceiptSchema,
	eventSchema,
	type SendEventMessage,
} from "../schemas/event";
import { fetchApi, fetchUpload, fetchVoid } from "./internal";

const EVENT_LIST_LIMIT = 100;

export const events = {
	// from/to bound the calendar window. The service matches on overlap, not
	// containment, so a multi-day camp comes back in every month it touches.
	// Results come oldest first and the service returns 20 unless asked for more
	// (100 is its cap), so an unbounded request would cut the upcoming events off.
	list: (
		clubId: string,
		params?: { event_type?: string; status?: string; from?: Date; to?: Date },
	) => {
		const searchParams = new URLSearchParams({ limit: String(EVENT_LIST_LIMIT) });
		if (params?.event_type) searchParams.set("event_type", params.event_type);
		if (params?.status) searchParams.set("status", params.status);
		if (params?.from) searchParams.set("from", params.from.toISOString());
		if (params?.to) searchParams.set("to", params.to.toISOString());
		return fetchApi(
			`/clubs/${clubId}/events?${searchParams.toString()}`,
			z.object({
				items: z.array(eventSchema),
				total: z.number(),
				limit: z.number(),
				offset: z.number(),
			}),
		);
	},
	get: (clubId: string, eventId: string) =>
		fetchApi(`/clubs/${clubId}/events/${eventId}`, eventSchema),
	create: (clubId: string, data: unknown) =>
		fetchApi(`/clubs/${clubId}/events`, eventSchema, {
			method: "POST",
			body: JSON.stringify(data),
		}),
	update: (clubId: string, eventId: string, data: unknown) =>
		fetchApi(`/clubs/${clubId}/events/${eventId}`, eventSchema, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	delete: (clubId: string, eventId: string) =>
		fetchVoid(`/clubs/${clubId}/events/${eventId}`, { method: "DELETE" }),
	rsvp: (clubId: string, eventId: string, status: string) =>
		fetchVoid(`/clubs/${clubId}/events/${eventId}/rsvp`, {
			method: "POST",
			body: JSON.stringify({ status }),
		}),
	uploadCoverImage: (clubId: string, eventId: string, file: File) => {
		const formData = new FormData();
		formData.append("cover", file);
		return fetchUpload(`/clubs/${clubId}/events/${eventId}/cover-image`, eventSchema, formData);
	},
	checkIn: (clubId: string, eventId: string) =>
		fetchVoid(`/clubs/${clubId}/events/${eventId}/checkin`, {
			method: "POST",
			body: JSON.stringify({}),
		}),
	invitees: {
		list: (clubId: string, eventId: string) =>
			fetchApi(`/clubs/${clubId}/events/${eventId}/invitees`, z.array(eventInviteeSchema)),
		add: (clubId: string, eventId: string, data: AddEventInvitees) =>
			fetchApi(`/clubs/${clubId}/events/${eventId}/invitees`, z.array(eventInviteeSchema), {
				method: "POST",
				body: JSON.stringify(data),
			}),
		revoke: (clubId: string, eventId: string, userId: string) =>
			fetchVoid(`/clubs/${clubId}/events/${eventId}/invitees/${encodeURIComponent(userId)}`, {
				method: "DELETE",
			}),
	},
	// A push to part of the guest list. The API answers 202: delivery is queued.
	sendMessage: (clubId: string, eventId: string, data: SendEventMessage) =>
		fetchApi(`/clubs/${clubId}/events/${eventId}/messages`, eventMessageReceiptSchema, {
			method: "POST",
			body: JSON.stringify(data),
		}),
};
